/**
 * TaskActivityTimeline - Timeline de andamentos/comentários da tarefa
 * 
 * Redesign completo seguindo padrão visual do modal de negócio:
 * - Formulário expansível com editor rico (RichTextEditor)
 * - Cards com avatar à esquerda + nome bold + data à direita
 * - Seção de comentários/respostas colapsável em cada card (TaskCommentReplySection)
 * - Agrupamento por data (Hoje, Ontem, etc.)
 * 
 * @version 2.0.0
 * RELEASE: 2026-04-07 v1.0.0 - Criação inicial replicando padrão do deal
 * RELEASE: 2026-04-07 v2.0.0 - Redesign: editor rico + layout cards + replies
 */
import React, { useState, useCallback, useRef, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Clock,
  Trash2,
  Pencil,
  Loader2,
  X,
  Plus,
  Check,
  Download,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import {
  MentionPickerField,
  type MentionableMember,
} from '@/components/shared/MentionPickerField';
import { AttachmentUploadArea } from '@/components/shared/AttachmentUploadArea';
import { AttachmentThumbnail } from '@/components/shared/AttachmentThumbnail';
import { ImageLightbox, type MediaItem } from '@/components/tenant/chat/ImageLightbox';
// Resolve o tipo de mídia do preview interno (imagem/vídeo/documento)
import { resolvePreviewMediaType } from '@/lib/attachment-preview';
import { TaskCommentReplySection } from './TaskCommentReplySection';
import { cn } from '@/lib/utils';
import { getMimeMediaType } from '@/lib/file-utils';
import { groupByDatePreserveOrder, DateGroup } from '@/lib/utils/group-by-date';
import { useAuth } from '@/hooks/useAuth';
import { usePermissionsContext } from '@/contexts/PermissionsContext';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { validateFile, uploadTaskAttachmentDirect } from '@/hooks/useTaskAttachments';
import { useToast } from '@/hooks/use-toast';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { DownloadAllZipButton } from '@/hooks/useDownloadAllAsZip';
import type { TaskComment } from '@/hooks/useTaskDetails';
import { TimelineActivityCard, TimelineDateSeparator } from '@/components/shared/timeline/TimelineActivityPrimitives';

/** Props do componente */
interface TaskActivityTimelineProps {
  /** ID da tarefa para vincular anexos */
  taskId: string;
  /** ID do tenant */
  tenantId: string;
  comments: TaskComment[];
  isLoading: boolean;
  onAddComment?: (params: { content: string; contentHtml?: string; mentionedUserIds?: string[] }) => Promise<string>;
  isAdding: boolean;
  onDeleteComment?: (commentId: string) => Promise<void>;
  /** Callback para editar andamento */
  onEditComment?: (params: { commentId: string; content: string; contentHtml?: string }) => Promise<void>;
  /** Se está editando andamento */
  isEditingComment?: boolean;
  /** Se pode comentar (tarefa não fechada) */
  canComment?: boolean;
}

/**
 * Detecta o tipo de badge para andamentos automáticos (source='system')
 * Retorna label e classes CSS baseado no conteúdo do andamento
 */
function getSystemBadgeInfo(content: string): { label: string; className: string } {
  if (content.includes('concluiu')) return { label: '✅ Concluída', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800' };
  if (content.includes('cancelou')) return { label: 'Cancelada', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800' };
  if (content.includes('reabriu')) return { label: 'Reaberta', className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800' };
  return { label: 'Automático', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800' };
}

/**
 * Timeline de andamentos da tarefa (padrão visual do modal de negócio)
 */
export function TaskActivityTimeline({
  taskId,
  tenantId,
  comments,
  isLoading,
  onAddComment,
  isAdding,
  onDeleteComment,
  onEditComment,
  isEditingComment,
  canComment = true,
}: TaskActivityTimelineProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { isAdmin } = usePermissionsContext();
  const { confirmDialog, ConfirmDialogComponent } = useConfirmDialog();
  const { toast } = useToast();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editorContent, setEditorContent] = useState('');
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  // RELEASE: 2026-09-12 - pessoas marcadas já na criação do andamento
  const [mentionedUsers, setMentionedUsers] = useState<MentionableMember[]>([]);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Estados do lightbox para visualização de imagens dos andamentos
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxMediaItems, setLightboxMediaItems] = useState<MediaItem[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Estados de edição inline de andamento
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  // Estados de edição de anexos do andamento
  const [editRemovedAttachmentIds, setEditRemovedAttachmentIds] = useState<string[]>([]);
  const [editStagedFiles, setEditStagedFiles] = useState<File[]>([]);
  const [isUploadingEditFiles, setIsUploadingEditFiles] = useState(false);
  const editFileInputRef = useRef<HTMLInputElement>(null);

  /** Busca anexos de todos os andamentos desta tarefa via RPC server-side */
  const commentAttachmentsQuery = useQuery({
    queryKey: ['task-comment-attachments', taskId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_task_attachments', {
        p_task_id: taskId,
        p_scope: 'comments',
      });
      if (error) {
        logger.error('Erro ao buscar anexos de andamentos via RPC:', error.message);
        return [];
      }
      // RPC retorna JSONB — parseia se necessário
      const attachments = (typeof data === 'string' ? JSON.parse(data) : data) as any[];
      return attachments || [];
    },
    enabled: !!taskId,
  });

  /** Mapa de anexos por comment_id para renderização rápida */
  const attachmentsByComment = React.useMemo(() => {
    const map: Record<string, typeof commentAttachmentsQuery.data> = {};
    (commentAttachmentsQuery.data || []).forEach((att: any) => {
      if (!att.task_comment_id) return;
      if (!map[att.task_comment_id]) map[att.task_comment_id] = [];
      map[att.task_comment_id]!.push(att);
    });
    return map;
  }, [commentAttachmentsQuery.data]);

  /** Adiciona arquivo ao staged */
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles: File[] = [];
    for (const file of files) {
      const validation = validateFile(file);
      if (!validation.valid) {
        toast({ title: 'Arquivo inválido', description: validation.error, variant: 'destructive' });
      } else {
        validFiles.push(file);
      }
    }
    setStagedFiles(prev => [...prev, ...validFiles]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [toast]);

  /** Remove arquivo staged */
  const removeStagedFile = useCallback((index: number) => {
    setStagedFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  /** Envia novo andamento com HTML + anexos */
  const handleSubmit = useCallback(async () => {
    const plainText = editorContent.replace(/<[^>]*>/g, '').trim();
    if (!plainText) return;

    try {
      // 1. Cria o comentário e obtém o ID
      const commentId = await onAddComment({
        content: plainText,
        contentHtml: editorContent,
        mentionedUserIds: mentionedUsers.map((u) => u.id),
      });

      // 2. Upload dos arquivos staged vinculados ao comentário
      if (stagedFiles.length > 0 && commentId) {
        setIsUploadingFiles(true);
        for (const file of stagedFiles) {
          try {
            // Passa commentId direto no insert — evita update separado
            await uploadTaskAttachmentDirect(taskId, tenantId, file, commentId);
          } catch (err: any) {
            logger.error('Erro ao enviar anexo do andamento:', err.message);
          }
        }
        setIsUploadingFiles(false);
        // Invalida cache de anexos
        commentAttachmentsQuery.refetch();
        queryClient.invalidateQueries({ queryKey: ['task-activity-attachments', taskId] });
      }

      setStagedFiles([]);
      setMentionedUsers([]);
      setEditorContent('');
      setIsFormOpen(false);
    } catch {
      // Erro já tratado pelo onAddComment
    }
  }, [editorContent, onAddComment, stagedFiles, mentionedUsers, taskId, tenantId, commentAttachmentsQuery]);

  /** Cancela formulário */
  const handleCancel = useCallback(() => {
    setEditorContent('');
    setMentionedUsers([]);
    setStagedFiles([]);
    setIsFormOpen(false);
  }, []);

  /** Inicia edição inline de um andamento */
  const handleStartEdit = useCallback((comment: TaskComment) => {
    setEditingCommentId(comment.id);
    setEditingContent(comment.content_html || comment.content);
    setEditRemovedAttachmentIds([]);
    setEditStagedFiles([]);
  }, []);

  /** Salva edição do andamento */
  /** Seleciona arquivos para edição de andamento */
  const handleEditFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles: File[] = [];
    for (const file of files) {
      const validation = validateFile(file);
      if (!validation.valid) {
        toast({ title: 'Arquivo inválido', description: validation.error, variant: 'destructive' });
      } else {
        validFiles.push(file);
      }
    }
    setEditStagedFiles(prev => [...prev, ...validFiles]);
    if (editFileInputRef.current) editFileInputRef.current.value = '';
  }, [toast]);

  const handleSaveEdit = useCallback(async () => {
    if (!editingCommentId || !onEditComment) return;
    const plainText = editingContent.replace(/<[^>]*>/g, '').trim();
    if (!plainText) return;
    try {
      // 1. Salvar texto
      await onEditComment({
        commentId: editingCommentId,
        content: plainText,
        contentHtml: editingContent,
      });

      // 2. Deletar anexos removidos (storage + registro)
      for (const attachmentId of editRemovedAttachmentIds) {
        try {
          const { data: att } = await supabase
            .from('task_attachments')
            .select('storage_path')
            .eq('id', attachmentId)
            .single();
          if (att?.storage_path) {
            await supabase.storage.from('tarefas').remove([att.storage_path]);
          }
          await supabase.from('task_attachments').delete().eq('id', attachmentId);
        } catch (err: any) {
          logger.error('Erro ao remover anexo na edição:', err.message);
        }
      }

      // 3. Upload de novos arquivos staged
      if (editStagedFiles.length > 0) {
        setIsUploadingEditFiles(true);
        for (const file of editStagedFiles) {
          try {
            await uploadTaskAttachmentDirect(taskId, tenantId, file, editingCommentId);
          } catch (err: any) {
            logger.error('Erro ao enviar novo anexo na edição:', err.message);
          }
        }
        setIsUploadingEditFiles(false);
      }

      // 4. Invalidar cache de anexos se houve alterações
      if (editRemovedAttachmentIds.length > 0 || editStagedFiles.length > 0) {
        commentAttachmentsQuery.refetch();
      }

      // 5. Invalidação final — garante que a timeline reflita texto + anexos atualizados
      queryClient.invalidateQueries({ queryKey: ['task-comments', taskId] });
      // Mantém aba "Anexos" agregada em sincronia após upload/remoção via andamento
      queryClient.invalidateQueries({ queryKey: ['task-activity-attachments', taskId] });

      setEditingCommentId(null);
      setEditingContent('');
      setEditRemovedAttachmentIds([]);
      setEditStagedFiles([]);
    } catch {
      // Erro já tratado pelo hook
    }
  }, [editingCommentId, editingContent, onEditComment, editRemovedAttachmentIds, editStagedFiles, taskId, tenantId, commentAttachmentsQuery]);

  /** Cancela edição inline */
  const handleCancelEdit = useCallback(() => {
    setEditingCommentId(null);
    setEditingContent('');
    setEditRemovedAttachmentIds([]);
    setEditStagedFiles([]);
  }, []);

  // Ordena comentários do mais recente ao mais antigo e agrupa por data
  const sortedComments = useMemo(() => 
    [...comments].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()),
    [comments]
  );
  const groupedComments = groupByDatePreserveOrder(sortedComments);

  // Loading
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="flex gap-3">
            <Skeleton className="h-8 w-8 rounded-full flex-shrink-0" />
            <div className="flex-1 space-y-2 p-3 rounded-lg border">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-3">
        {/* ============================================================ */}
        {/* Formulário de novo andamento — estado colapsado/expandido */}
        {/* ============================================================ */}
        {!onAddComment ? null : !isFormOpen ? (
          /* Estado colapsado: botão "Adicionar andamento..." */
          <button
            type="button"
            onClick={() => setIsFormOpen(true)}
            className={cn(
              "w-full flex items-center gap-3 p-3 rounded-lg border border-dashed",
              "text-muted-foreground hover:text-foreground hover:border-primary/40",
              "hover:bg-muted/30 transition-all cursor-text text-left"
            )}
          >
            <SmartAvatar
              name={user?.user_metadata?.full_name || user?.email}
              src={user?.user_metadata?.avatar_url}
              size="sm"
              className="flex-shrink-0"
            />
            <span className="text-sm">Adicionar andamento...</span>
          </button>
        ) : (
          /* Estado expandido: formulário com editor rico */
          <div className="rounded-lg border bg-card shadow-sm">
            {/* Header do formulário */}
            <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/30 rounded-t-lg">
              <div className="flex items-center gap-2">
                <Plus className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">Novo Andamento</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={handleCancel}
                  disabled={isAdding}
                >
                  <X className="h-3.5 w-3.5 mr-1" />
                  Cancelar
                </Button>
                <Button
                  size="sm"
                  className="h-7 text-xs"
                  onClick={handleSubmit}
                  disabled={isAdding || !editorContent.replace(/<[^>]*>/g, '').trim()}
                >
                  {isAdding ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                  ) : (
                    <Plus className="h-3.5 w-3.5 mr-1" />
                  )}
                  Criar
                </Button>
              </div>
            </div>

            {/* Editor rico */}
            <div className="p-2">
              <RichTextEditor
                content={editorContent}
                onChange={setEditorContent}
                placeholder="Descreva o andamento..."
                minHeight="100px"
                maxHeight="300px"
                fontSize="sm"
                showToolbar={true}
                autoFocus={true}
              />

              {/* Menções (RELEASE 2026-09-12) */}
              <div className="mt-2">
                <MentionPickerField
                  value={mentionedUsers}
                  onChange={setMentionedUsers}
                  disabled={isUploadingFiles}
                />
              </div>
            </div>

            {/* Área de anexos staged */}
            <div className="px-2 pb-2 space-y-2">
              {/* Thumbnails dos arquivos selecionados */}
              {stagedFiles.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {stagedFiles.map((file, idx) => (
                    <div key={`${file.name}-${idx}`} className="relative group">
                      <div className="w-14 h-14 rounded-lg border bg-muted/30 flex items-center justify-center overflow-hidden">
                        {file.type.startsWith('image/') ? (
                          <img
                            src={URL.createObjectURL(file)}
                            alt={file.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-[9px] text-muted-foreground text-center px-1 truncate">
                            {file.name.split('.').pop()?.toUpperCase()}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => removeStagedFile(idx)}
                        className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-destructive text-white flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                      <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                        <div className="bg-popover text-popover-foreground text-[9px] px-1.5 py-0.5 rounded shadow-lg whitespace-nowrap max-w-[100px] truncate">
                          {file.name}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Botão de upload */}
              <AttachmentUploadArea
                onUploadClick={() => fileInputRef.current?.click()}
                isUploading={isUploadingFiles}
                maxSizeLabel="Máx: 50MB por arquivo"
              />

              {/* Input file oculto */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* Estado vazio */}
        {/* ============================================================ */}
        {comments.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <div className="h-12 w-12 rounded-full bg-muted/50 flex items-center justify-center mb-3">
              <Clock className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-muted-foreground">
              Nenhum andamento registrado
            </p>
            <p className="text-xs text-muted-foreground/70 mt-1">
              Adicione notas e atualizações sobre esta tarefa
            </p>
          </div>
        )}

        {/* ============================================================ */}
        {/* Timeline agrupada por data */}
        {/* ============================================================ */}
        {comments.length > 0 && (
          <div className="space-y-1">
            {groupedComments.map((group: DateGroup<TaskComment>) => (
              <div key={group.dateKey}>
                {/* Separador de data */}
                <TimelineDateSeparator label={group.dateLabel} />

                {/* Cards do grupo */}
                <div className="space-y-3">
                  {group.items.map((comment) => (
                    <TimelineActivityCard
                      key={comment.id}
                      avatarSrc={comment.user_avatar}
                      avatarName={comment.user_name}
                      author={comment.source === 'system' ? (
                              /* Andamento automático: nome "Sistema" + badge colorido */
                              <>
                                <span className="text-xs font-semibold text-foreground">Sistema</span>
                                <span className="text-[10px] text-muted-foreground">(por: {comment.user_name || 'Usuário'})</span>
                                <Badge variant="outline" className={cn('text-[9px] px-1.5 py-0 h-4 font-medium border', getSystemBadgeInfo(comment.content).className)}>
                                  {getSystemBadgeInfo(comment.content).label}
                                </Badge>
                              </>
                            ) : (
                              <span className="text-xs font-semibold text-foreground truncate">
                                {comment.user_name || 'Usuário'}
                              </span>
                            )}
                      actions={(comment.user_id === user?.id || isAdmin) && comment.source !== 'system' && canComment ? (
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
                                {/* Botão editar */}
                                {onEditComment && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 text-muted-foreground hover:text-primary hover:bg-primary/10"
                                        onClick={() => handleStartEdit(comment)}
                                      >
                                        <Pencil className="h-3.5 w-3.5" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="text-xs">
                                      Editar
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                                {/* Botão excluir */}
                                {onDeleteComment && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-6 w-6 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                        onClick={async () => {
                                          const confirmed = await confirmDialog({
                                            title: 'Excluir andamento?',
                                            description: 'Esta ação não pode ser desfeita.',
                                            variant: 'danger',
                                            confirmText: 'Excluir',
                                            cancelText: 'Cancelar',
                                          });
                                          if (confirmed) onDeleteComment(comment.id);
                                        }}
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="text-xs">
                                      Excluir
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                              </div>
                            ) : undefined}
                      timestamp={<span className="text-xs text-muted-foreground whitespace-nowrap">
                              {format(new Date(comment.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                              {/* Indicador "(editado)" */}
                              {new Date(comment.updated_at).getTime() > new Date(comment.created_at).getTime() + 1000 && (
                                <span className="ml-1 text-[10px] text-muted-foreground/70 italic">(editado)</span>
                              )}
                            </span>}
                      footer={comment.source !== 'system' ? (
                        <div className="px-3 pb-2 pt-1">
                          <TaskCommentReplySection
                            taskCommentId={comment.id}
                            taskId={taskId}
                            tenantId={tenantId}
                            repliesCount={comment.replies_count ?? 0}
                            defaultExpanded={(comment.replies_count ?? 0) > 0}
                            disabled={!canComment}
                          />
                        </div>
                      ) : undefined}
                    >
                        {/* Conteúdo do andamento — modo edição ou visualização */}
                          {editingCommentId === comment.id ? (
                            /* Modo edição inline */
                            <div className="space-y-2">
                              <RichTextEditor
                                content={editingContent}
                                onChange={setEditingContent}
                                placeholder="Editar andamento..."
                                minHeight="80px"
                                maxHeight="250px"
                                fontSize="sm"
                                showToolbar={true}
                                autoFocus={true}
                              />

                              {/* Anexos existentes (editáveis — com botão remover) */}
                              {(() => {
                                const existingAtts = (attachmentsByComment[comment.id] || [])
                                  .filter((att: any) => !editRemovedAttachmentIds.includes(att.id));
                                return existingAtts.length > 0 ? (
                                  <div className="flex flex-wrap gap-2">
                                    {existingAtts.map((att: any) => (
                                      <AttachmentThumbnail
                                        key={att.id}
                                        attachment={{
                                          id: att.id,
                                          file_name: att.file_name,
                                          file_type: att.file_type,
                                          public_url: att.public_url,
                                          thumbnail_url: att.thumbnail_url,
                                          file_size: att.file_size,
                                        }}
                                        readOnly={false}
                                        onDelete={() => setEditRemovedAttachmentIds(prev => [...prev, att.id])}
                                      />
                                    ))}
                                  </div>
                                ) : null;
                              })()}

                              {/* Novos arquivos staged na edição */}
                              {editStagedFiles.length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                  {editStagedFiles.map((file, idx) => (
                                    <div key={`edit-staged-${idx}`} className="relative group">
                                      <div className="w-14 h-14 rounded-lg border bg-muted/30 flex items-center justify-center overflow-hidden">
                                        {file.type.startsWith('image/') ? (
                                          <img
                                            src={URL.createObjectURL(file)}
                                            alt={file.name}
                                            className="w-full h-full object-cover"
                                          />
                                        ) : (
                                          <span className="text-[9px] text-muted-foreground text-center px-1 truncate">
                                            {file.name.split('.').pop()?.toUpperCase()}
                                          </span>
                                        )}
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => setEditStagedFiles(prev => prev.filter((_, i) => i !== idx))}
                                        className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-destructive text-white flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity"
                                      >
                                        <X className="h-2.5 w-2.5" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Botão de adicionar anexo na edição */}
                              <AttachmentUploadArea
                                onUploadClick={() => editFileInputRef.current?.click()}
                                isUploading={isUploadingEditFiles}
                                maxSizeLabel="Máx: 50MB por arquivo"
                              />
                              <input
                                ref={editFileInputRef}
                                type="file"
                                multiple
                                className="hidden"
                                onChange={handleEditFileSelect}
                              />

                              <div className="flex items-center gap-1.5 justify-end">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 text-xs"
                                  onClick={handleCancelEdit}
                                  disabled={isEditingComment || isUploadingEditFiles}
                                >
                                  <X className="h-3.5 w-3.5 mr-1" />
                                  Cancelar
                                </Button>
                                <Button
                                  size="sm"
                                  className="h-7 text-xs"
                                  onClick={handleSaveEdit}
                                  disabled={isEditingComment || isUploadingEditFiles || !editingContent.replace(/<[^>]*>/g, '').trim()}
                                >
                                  {(isEditingComment || isUploadingEditFiles) ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                                  ) : (
                                    <Check className="h-3.5 w-3.5 mr-1" />
                                  )}
                                  Salvar
                                </Button>
                              </div>
                            </div>
                          ) : (
                            /* Modo visualização */
                            <div className="bg-muted/60 rounded-md px-3 py-2 text-xs text-foreground [&_*]:text-foreground">
                              {comment.content_html ? (
                                <div
                                  className="prose prose-xs dark:prose-invert max-w-none text-xs"
                                  dangerouslySetInnerHTML={{ __html: comment.content_html }}
                                />
                              ) : (
                                <p className="whitespace-pre-wrap">
                                  {comment.content}
                                </p>
                              )}
                            </div>
                          )}

                          {/* Anexos do andamento (modo visualização — somente quando NÃO está editando) */}
                          {editingCommentId !== comment.id && attachmentsByComment[comment.id]?.length > 0 && (
                            <div className="mt-2">
                              {/* Botão para baixar todos os anexos do andamento como ZIP */}
                              <div className="flex justify-end mb-1">
                                <DownloadAllZipButton
                                  attachments={attachmentsByComment[comment.id]!}
                                  zipFileName={`anexos_andamento_${comment.id.substring(0, 8)}.zip`}
                                />
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {attachmentsByComment[comment.id]!.map((att: any) => (
                                  <AttachmentThumbnail
                                    key={att.id}
                                    attachment={{
                                      id: att.id,
                                      file_name: att.file_name,
                                      file_type: att.file_type,
                                      public_url: att.public_url,
                                      thumbnail_url: att.thumbnail_url,
                                      file_size: att.file_size,
                                    }}
                                    readOnly
                                    onClick={() => {
                                      const commentAtts = attachmentsByComment[comment.id] || [];
                                      const items: MediaItem[] = commentAtts.map((a: any) => ({
                                        url: a.public_url,
                                        type: resolvePreviewMediaType(a.file_type, a.mime_type) ?? 'document',
                                        title: a.file_name,
                                      }));
                                      const idx = commentAtts.findIndex((a: any) => a.id === att.id);
                                      setLightboxMediaItems(items);
                                      setLightboxIndex(idx >= 0 ? idx : 0);
                                      setLightboxOpen(true);
                                    }}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                    </TimelineActivityCard>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Lightbox para visualização de imagens dos andamentos */}
      <ImageLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        mediaItems={lightboxMediaItems}
        initialIndex={lightboxIndex}
      />
      {/* Diálogo de confirmação de exclusão */}
      {ConfirmDialogComponent}
    </TooltipProvider>
  );
}
